import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { useAppSelector } from 'app/redux';
import {
  HarvestLineType,
  HaulingLineType,
  LineItemStatus,
  MiscLineType,
  TransferLineType,
} from 'common/models/harvestData/payrollData';
import { RemoveButton } from 'common/styles/button';
import { tableHeaderGrey } from 'common/styles/colors';
import currency from 'currency.js';
import { usePickTable } from 'features/harvest-payroll/hooks/usePickTable';
import { useTransferTable } from 'features/harvest-payroll/hooks/useTransferTable';
import {
  PayrollPageState,
  payrollSlice,
  payrollSliceName,
} from 'features/harvest-payroll/PayrollSlice';
import {
  PayrollTableRow,
  RowMetadata,
  totalIdKey,
} from 'features/harvest-payroll/utils/payrollDataTypes';
import { isRemovableRow } from 'features/harvest-payroll/utils/payrollPageUtils';
import { FC } from 'react';
import { useDispatch } from 'react-redux';
import { PayrollInput } from './PayrollInput';
import {
  EmptyCell,
  QuantityCell,
  RateCell,
  RemoveRowCell,
  SubtotalCell,
  TableCell,
  ValueChangedWrap,
} from './styles';

export const PayrollTableLine: FC<{
  row: PayrollTableRow;
  changeBackground: boolean;
}> = ({ row, changeBackground }) => {
  const dispatch = useDispatch();
  const { removePickRow, removeMiscRow } = usePickTable();
  const { removeTransfer } = useTransferTable();
  const { isEditable, isDirty } = useAppSelector<PayrollPageState>(
    state => state[payrollSliceName],
  );
  const {
    pickDay,
    pick,
    block,
    quantity,
    metadata: { descCategory, descType },
    description,
    rate,
    subtotal,
  } = row;
  const isPickLine =
    descType === HarvestLineType[HarvestLineType.Harvesting] ||
    descType === HaulingLineType[HaulingLineType.Hauling];
  const isCommissionLine =
    descType === HarvestLineType[HarvestLineType.Commission] ||
    descType === HaulingLineType[HaulingLineType.Commission];
  const isTransferLine =
    descCategory === TransferLineType[TransferLineType.Transfer];
  const isTotalLine = descType === totalIdKey;
  const hasEditableFields =
    descType === HarvestLineType[HarvestLineType.NPT] ||
    descType === HarvestLineType[HarvestLineType.Breaks];
  const isNew =
    row.metadata.status === LineItemStatus.Initial && isPickLine && pickDay;
  const wasPickEdited = row.metadata.status === LineItemStatus.Edited;

  const parsedQuantity = parseInt(quantity, 10);
  const parsedRate = currency(rate).value;

  /** Form submission handler to remove a table row. */
  const handleRemoveClick = (data: RowMetadata) => {
    const { descCategory } = data;

    switch (descCategory) {
      case HarvestLineType[HarvestLineType.Harvesting]:
      case HaulingLineType[HaulingLineType.Hauling]:
        removePickRow(data);
        break;

      case MiscLineType[MiscLineType.MISC]:
        removeMiscRow(data);
        break;

      case TransferLineType[TransferLineType.Transfer]:
        removeTransfer(data);
        break;

      default:
      // do nothing
    }

    if (!isDirty) {
      dispatch(payrollSlice.actions.setIsDirty(true));
    }
  };

  const handleWasEdited = (value: string, noWrap = false) => {
    return wasPickEdited && !noWrap ? (
      <ValueChangedWrap>{value}</ValueChangedWrap>
    ) : (
      value
    );
  };

  return (
    <>
      <TableCell
        $changeBackground={isTransferLine && changeBackground}
        $isEditable={isEditable}
        {...(isNew && { $isNew: ['left', 'top', 'bottom'] })}
      >
        {pickDay}
      </TableCell>
      <TableCell
        $changeBackground={isTransferLine && changeBackground}
        $isEditable={isEditable}
        {...(isNew && { $isNew: ['top', 'bottom'] })}
      >
        {pick}
      </TableCell>
      <TableCell
        $changeBackground={isTransferLine && changeBackground}
        $isEditable={isEditable}
        {...(isNew && { $isNew: ['top', 'bottom', 'right'] })}
      >
        {block}
      </TableCell>
      <QuantityCell
        $changeBackground={changeBackground}
        $isEditable={isEditable}
      >
        {hasEditableFields && isEditable ? (
          <PayrollInput
            rowMetadata={row.metadata}
            cellKey='quantity'
            defaultValue={
              typeof parsedQuantity === 'number' ? parsedQuantity : 0
            }
          />
        ) : (
          handleWasEdited(quantity, isCommissionLine)
        )}
      </QuantityCell>
      <TableCell $changeBackground={changeBackground} $isEditable={isEditable}>
        {descType === MiscLineType[MiscLineType.MISC]
          ? `${MiscLineType[MiscLineType.MISC]} - ${description}`
          : description}
      </TableCell>
      <RateCell
        $isTotalLine={isTotalLine}
        $changeBackground={changeBackground}
        $isEditable={isEditable}
      >
        {hasEditableFields && isEditable ? (
          <PayrollInput
            rowMetadata={row.metadata}
            cellKey='rate'
            defaultValue={typeof parsedRate === 'number' ? parsedRate : 0}
          />
        ) : (
          handleWasEdited(rate)
        )}
      </RateCell>
      <SubtotalCell
        $isTotalLine={isTotalLine}
        $changeBackground={changeBackground}
        $isEditable={isEditable}
      >
        {handleWasEdited(subtotal)}
      </SubtotalCell>
      {isEditable && isRemovableRow(descType) ? (
        <RemoveRowCell
          $changeBackground={changeBackground}
          $isEditable={isEditable}
        >
          <RemoveButton onClick={() => handleRemoveClick(row.metadata)}>
            <CancelOutlinedIcon htmlColor={tableHeaderGrey} />
          </RemoveButton>
        </RemoveRowCell>
      ) : (
        <EmptyCell
          $changeBackground={changeBackground}
          $isEditable={isEditable}
        />
      )}
    </>
  );
};
